/**
 * Sign-in, sign-up, and the states between them.
 *
 * Clerk renders the form itself — it owns bot protection, verification codes, and new-device
 * challenges — but it is stripped of its own frame and dropped into this page, so signing in
 * looks like part of the application rather than a third-party box. The styling lives in
 * `clerk-appearance.ts`.
 */

import type { ReactNode } from 'react';
import { SignIn, SignUp } from '@clerk/clerk-react';

import { RECORDABLE_APPEARANCES } from '../ui/attendance.js';
import { Brandmark } from '../ui/Brandmark.js';

/** The four states a roll call records, shown as a quiet reminder of what the app does. */
function StatePreview(): ReactNode {
  return (
    <div className="flex items-center gap-2" aria-hidden="true">
      {RECORDABLE_APPEARANCES.map((appearance) => {
        const { Icon } = appearance;
        return (
          <span
            key={appearance.state}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${appearance.solid}`}
          >
            {Icon === null ? null : <Icon size={17} />}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Frame shared by the authentication pages.
 *
 * A single column: the mark and name, a sentence of content, then the form on the app's own
 * surface behind one hairline. No box around the form — the page is the frame.
 */
function AuthFrame({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="flex min-h-full flex-col">
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-7 px-5 py-10">
        <div>
          <div className="flex items-center gap-3">
            <Brandmark size={44} />
            <h1 className="text-display font-bold tracking-tight">Anwesenheit</h1>
          </div>
          <p className="mt-3 max-w-prose text-base text-ink-2">
            Take attendance for a class, a team, or a course — one tap per person, and the
            numbers worked out for you.
          </p>
          <div className="mt-4"><StatePreview /></div>
        </div>

        <div className="border-t border-line pt-7">{children}</div>
      </main>
    </div>
  );
}

/** The sign-in page. */
export function SignInPage(): ReactNode {
  return (
    <AuthFrame>
      <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" forceRedirectUrl="/" />
    </AuthFrame>
  );
}

/** The sign-up page, reached from the sign-in form. */
export function SignUpPage(): ReactNode {
  return (
    <AuthFrame>
      <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" forceRedirectUrl="/" />
    </AuthFrame>
  );
}

/** Shown while the authentication provider is still loading and nothing is cached. */
export function AuthLoadingPage(): ReactNode {
  return (
    <AuthFrame>
      <p className="text-ink-2">Checking your session…</p>
      <p className="mt-2 text-tiny text-ink-3">
        If this stays here you are probably offline and this device has not signed in before.
        Sign in once with a connection and everything afterwards works offline.
      </p>
    </AuthFrame>
  );
}
